import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { TrendingUp, Clock, Zap, DollarSign, Quote } from 'lucide-react'

const Results = () => {
  const { t } = useTranslation()
  const stats = [
    {
      icon: <Clock className="w-8 h-8 text-purple-400" />,
      value: "20-40",
      label: t('results.hoursLabel', "Hours Saved"),
      sublabel: t('results.hoursSub', "Per week per client")
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-purple-400" />,
      value: "94.7%",
      label: t('results.dataLabel', "Data Entry"),
      sublabel: t('results.dataSub', "Reduction in manual work")
    },
    {
      icon: <Zap className="w-8 h-8 text-purple-400" />,
      value: "3-5x",
      label: t('results.responseLabel', "Faster Response"),
      sublabel: t('results.responseSub', "For hot leads")
    },
    {
      icon: <DollarSign className="w-8 h-8 text-purple-400" />,
      value: "$15K+",
      label: t('results.revenueLabel', "Revenue"),
      sublabel: t('results.revenueSub', "Recovered from lost clients")
    }
  ]

  const testimonials = [
    {
      quote: t('results.testimonial1', "Nodatx gave us a system that runs 24/7. We used to spend 3 days chasing leads, now it takes us 15 minutes. Best investment we've made."),
      name: t('results.testimonial1Name', "Ahmed Mahmoud"),
      role: t('results.testimonial1Role', "CEO, Tech Company"),
      initials: "AM"
    },
    {
      quote: t('results.testimonial2', "Working with Nodatx saved us incredible time and effort! Tasks that used to take hours now complete automatically in seconds. Amazing work."),
      name: t('results.testimonial2Name', "Sara Gamal"),
      role: t('results.testimonial2Role', "Marketing Director, Creative Agency"),
      initials: "SG"
    },
    {
      quote: t('results.testimonial3', "The system they built reduced errors to near zero and connected all our tools together. Extremely professional - I highly recommend them to anyone looking to scale."),
      name: t('results.testimonial3Name', "Mostafa Kamal"),
      role: t('results.testimonial3Role', "Founder, E-commerce Store"),
      initials: "MK"
    }
  ]

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.45,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  }

  return (
    <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0f0f1a] relative">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('results.badge', 'Results')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 sm:mt-4 mb-4 sm:mb-6 text-white">
            {t('results.heading1', 'Real Results.')}{' '}
            <span className="text-purple-400">{t('results.heading2', 'Real Time Saved.')}</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('results.description', 'Numbers that speak for themselves. What our clients achieve.')}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              className="text-center group p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-purple-500/50 transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                {stat.icon}
              </div>
              
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-purple-400 mb-2">
                {stat.value}
              </div>
              
              <div className="text-white font-semibold text-base sm:text-lg mb-1">
                {stat.label}
              </div>
              
              <div className="text-gray-500 text-sm">
                {stat.sublabel}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mt-16 sm:mt-24">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('results.testimonialHeading', 'What They Say About Us')}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                custom={idx}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                className="bg-gradient-to-b from-[#1a1a2e]/50 to-[#0a0a0f] rounded-2xl p-6 border border-white/5 hover:border-purple-500/30 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <Quote className="w-8 h-8 text-purple-500/50 mb-4" />
                  <p className="text-gray-300 mb-6 italic text-sm sm:text-base leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                </div>
                <div className="flex items-center gap-4 mt-auto border-t border-white/10 pt-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-400 rounded-full flex items-center justify-center text-white font-bold text-base shrink-0">
                    {testimonial.initials}
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{testimonial.name}</div>
                    <div className="text-gray-400 text-xs">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Results